/**
 * TreatmentRequestChangeSection - Section wrapper for treatment request change cards
 *
 * Matches the ProviderChangeSection pattern (title + count badge + sorting).
 * Sorts changed requests (CREATED, UPDATED, DELETED) above SAME ones.
 */

import { useMemo } from 'react';

import { Badge } from '@/components/ui/catalyst/badge';
import type { ChangedTreatmentRequestEntity } from '@/services/api/generated/crossMatterChangesService';

import { CHANGE_TYPE_ORDER } from './changeTypeConfig';
import { TreatmentRequestChangeCard } from './TreatmentRequestChangeCard';

interface TreatmentRequestChangeSectionProps {
  treatmentRequests: ChangedTreatmentRequestEntity[];
}

export const TreatmentRequestChangeSection = ({
  treatmentRequests,
}: TreatmentRequestChangeSectionProps) => {
  const sorted = useMemo(
    () =>
      [...treatmentRequests].sort(
        (a, b) =>
          CHANGE_TYPE_ORDER[a.change_type] -
          CHANGE_TYPE_ORDER[b.change_type],
      ),
    [treatmentRequests],
  );

  if (treatmentRequests.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <h3 className="text-base font-semibold text-gray-900">
          Treatment Requests
        </h3>
        <Badge color="zinc" className="text-xs">
          {treatmentRequests.length}
        </Badge>
      </div>
      <div className="space-y-2">
        {sorted.map((request) => (
          <TreatmentRequestChangeCard
            key={request.entity_id}
            entity={request}
          />
        ))}
      </div>
    </div>
  );
};
